import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import {
  Calendar, Zap, Trophy, Brain, Layers, Target,
  ChevronRight, ChevronLeft, Sparkles
} from 'lucide-react';
import api from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

export default function OnboardingWalkthrough({ onComplete }) {
  const [step, setStep] = useState(0);
  const [trialDate, setTrialDate] = useState('May 31, 2026');

  useEffect(() => {
    api.get('/api/config/trial').then(res => {
      const d = new Date(res.data.trial_end_date + 'T00:00:00');
      setTrialDate(d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    }).catch(() => {});
  }, []);

  const handleComplete = async () => {
    try {
      await api.post('/api/users/me/onboarding-complete');
    } catch (e) {
      // non-blocking
    }
    onComplete();
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const steps = [
    // Step 0: Welcome
    {
      icon: Sparkles,
      iconColor: 'text-[hsl(var(--brand-gold-500))]',
      iconBg: 'bg-[hsl(var(--brand-gold-500))]/20',
      title: 'Welcome to JepPrep!',
      subtitle: 'The best Jeopardy-style trivia training platform',
      content: (
        <div className="space-y-4 text-center">
          <img src={LOGO_URL} alt="Jeopardy practice" className="h-20 mx-auto" />
          <p className="text-gray-300 text-sm leading-relaxed">
            Create AI-generated Boards, compete on Leaderboards, and track your progress with detailed analytics.
          </p>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
            <p className="text-emerald-400 text-sm font-medium">
              You have unlimited access until {trialDate}
            </p>
          </div>
        </div>
      )
    },
    // Step 1: Daily Challenge
    {
      icon: Calendar,
      iconColor: 'text-cyan-400',
      iconBg: 'bg-cyan-500/20',
      title: 'Daily Challenge',
      subtitle: '10 fresh clues every day',
      content: (
        <div className="space-y-3">
          <p className="text-gray-300 text-sm leading-relaxed">
            Every day brings a new set of 10 clues across 5 categories. Answer them all to build
            your streak and climb the global leaderboard.
          </p>
          <div className="space-y-2">
            {[
              { label: 'Build Your Streak', desc: 'Come back every day to keep your streak alive. The longer you go, the higher you climb.' },
              { label: 'Global Leaderboards', desc: 'Compete with players worldwide on Today, This Week, and All-Time rankings.' },
              { label: 'Earn Achievements', desc: 'Unlock badges and achievements as you master categories and improve your accuracy.' },
            ].map(f => (
              <div key={f.label} className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3">
                <p className="text-white text-sm font-medium">{f.label}</p>
                <p className="text-gray-400 text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    // Step 2: Leaderboards
    {
      icon: Trophy,
      iconColor: 'text-[hsl(var(--brand-gold-500))]',
      iconBg: 'bg-[hsl(var(--brand-gold-500))]/20',
      title: 'Leaderboards',
      subtitle: 'Compete globally',
      content: (
        <div className="space-y-3">
          <p className="text-gray-300 text-sm leading-relaxed">
            See how you stack up against the world. Track your ranking across Today,
            This Week, and All-Time leaderboards.
          </p>
          <div className="space-y-2">
            {[
              { label: 'Podium Display', desc: 'Top 3 players get featured on the podium with their avatars and scores.' },
              { label: 'Track Your Rank', desc: 'Your position is highlighted so you always know where you stand.' },
              { label: 'Multiple Timeframes', desc: 'Filter by Today, This Week, and All-Time to see different competitive windows.' },
            ].map(f => (
              <div key={f.label} className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3">
                <p className="text-white text-sm font-medium">{f.label}</p>
                <p className="text-gray-400 text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    // Step 3: Sprint Mode
    {
      icon: Zap,
      iconColor: 'text-emerald-400',
      iconBg: 'bg-emerald-500/20',
      title: 'Sprint Mode',
      subtitle: 'Rapid-fire timed practice',
      content: (
        <div className="space-y-3">
          <p className="text-gray-300 text-sm leading-relaxed">
            Push your limits with timed solo practice sessions. Choose 15, 25, or 50 questions
            and race against a 20-second timer for each clue.
          </p>
          <div className="space-y-2">
            {[
              { label: '20-Second Timer', desc: 'Each clue gets a countdown. Answer before time runs out to maximize your score.' },
              { label: 'Category Analysis', desc: 'After each sprint, see a complete breakdown of your performance by category.' },
              { label: 'Identify Weaknesses', desc: 'AI-paraphrased clues from 514K+ questions ensure every sprint feels fresh.' },
            ].map(f => (
              <div key={f.label} className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3">
                <p className="text-white text-sm font-medium">{f.label}</p>
                <p className="text-gray-400 text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    // Step 4: Daily Categories
    {
      icon: Layers,
      iconColor: 'text-purple-400',
      iconBg: 'bg-purple-500/20',
      title: 'Daily Categories',
      subtitle: '3 featured categories daily',
      content: (
        <div className="space-y-3">
          <p className="text-gray-300 text-sm leading-relaxed">
            Each day features 3 new categories. Choose your difficulty and session length (5, 10, 15, or 25 clues)
            and dive deep into specific topics.
          </p>
          <div className="space-y-2">
            {[
              { label: 'AI-Paraphrased Clues', desc: 'Every clue is uniquely rephrased so you never see the same question twice.' },
              { label: 'Cross-Mode Stats', desc: 'Your performance carries over to your overall stats, leaderboards, and AskAlex analytics.' },
              { label: 'Difficulty Selection', desc: 'Choose College, Adult, or Masters difficulty to match your skill level.' },
            ].map(f => (
              <div key={f.label} className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3">
                <p className="text-white text-sm font-medium">{f.label}</p>
                <p className="text-gray-400 text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    // Step 5: AskAlex
    {
      icon: Brain,
      iconColor: 'text-amber-400',
      iconBg: 'bg-amber-500/20',
      title: 'AskAlex',
      subtitle: 'Your personal trivia coach',
      content: (
        <div className="space-y-3">
          <p className="text-gray-300 text-sm leading-relaxed">
            Data-driven missions, Clue Explorer search across 514K+ clues, and a knowledge portfolio
            that tracks your expertise across every category.
          </p>
          <div className="space-y-2">
            {[
              { label: 'Data Missions', desc: '12 unique missions analyzing real trivia patterns. Earn XP and badges as you complete them.' },
              { label: 'Clue Explorer', desc: 'Search and filter the entire clue database. Discover patterns, top answers, and find categories to practice.' },
              { label: 'Knowledge Portfolio', desc: 'Track your expertise across all categories with visual breakdowns and progress tracking.' },
            ].map(f => (
              <div key={f.label} className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3">
                <p className="text-white text-sm font-medium">{f.label}</p>
                <p className="text-gray-400 text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    // Step 6: Create a Board
    {
      icon: Target,
      iconColor: 'text-rose-400',
      iconBg: 'bg-rose-500/20',
      title: 'Create a Board',
      subtitle: 'AI-powered Jeopardy boards',
      content: (
        <div className="space-y-3">
          <p className="text-gray-300 text-sm leading-relaxed">
            Generate complete Jeopardy-style boards with AI. Every clue is unique, 
            fact-checked, and formatted in authentic Jeopardy style with 3-round gameplay.
          </p>
          <div className="space-y-2">
            {[
              { label: 'AI-Generated Clues', desc: 'Powered by advanced AI. Each board has 6 categories, 30 clues, and a Final Round wager.' },
              { label: '3-Round Gameplay', desc: 'Round 1, Round 2 with doubled values, and a Final Round where you risk it all.' },
              { label: 'Beat the Score', desc: 'Challenge yourself to beat your personal best and share your results.' },
            ].map(f => (
              <div key={f.label} className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3">
                <p className="text-white text-sm font-medium">{f.label}</p>
                <p className="text-gray-400 text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
  ];

  const currentStep = steps[step];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="onboarding-walkthrough">
      <div className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${currentStep.iconBg} flex items-center justify-center`}>
              <currentStep.icon className={`w-5 h-5 ${currentStep.iconColor}`} />
            </div>
            <div>
              <h2 className="text-white font-bold text-base">{currentStep.title}</h2>
              <p className="text-gray-400 text-xs">{currentStep.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Step indicators */}
        <div className="px-5 pb-3 flex gap-1 shrink-0">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-all ${
                i <= step ? 'bg-[hsl(var(--brand-gold-500))]' : 'bg-[hsl(var(--border))]'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="px-5 pb-3 flex-1 overflow-y-auto">
          {currentStep.content}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[hsl(var(--border))] flex items-center justify-between shrink-0">
          <div>
            {step > 0 ? (
              <Button
                variant="ghost"
                onClick={handleBack}
                className="text-gray-400 hover:text-white"
                data-testid="onboarding-back"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            ) : (
              <span className="text-gray-500 text-xs">{step + 1} of {steps.length}</span>
            )}
          </div>
          <Button
            onClick={handleNext}
            className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold hover:bg-[hsl(var(--brand-gold-400))]"
            data-testid="onboarding-next"
          >
            {step === steps.length - 1 ? (
              <>
                Go to Dashboard
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
